/**
 * The roots an address may name. Declared in one place because more than one reader answers from
 * them — the resolver, in both spellings, and the `pattern` a blueprint validates a path with — so adding
 * a root is a change here, and the resolver's map does not compile until it answers it.
 */
export const SCHEMA_ADDRESS_ROOTS = ['state', 'data', 'context', 'source'] as const;

export type SchemaAddressRoot = (typeof SCHEMA_ADDRESS_ROOTS)[number];

/**
 * What a blueprint validates a path field with, derived from the roots rather than written out twice.
 * `schema-drift-guard.spec.ts` asserts every address field in `json-schemas/` carries exactly this
 * string, so a root added above cannot leave the blueprints behind, and `schema-addresses.spec.ts`
 * holds every address a schema writes to it.
 *
 * Every path names its root. One naming none read as `$data.`; it left the grammar in 655195, and the
 * resolver stopped answering it in 662168.
 */
export const SCHEMA_PATH_PATTERN = `^\\$(${SCHEMA_ADDRESS_ROOTS.join('|')})(\\.[A-Za-z0-9_]+)*$`;

/** An address the recogniser read: as written, its root, and the keys after it. */
export interface SchemaAddress {
  readonly text: string;
  readonly root: string;
  readonly keys: readonly string[];
}

/**
 * The shape of an address, independent of the root table: `$`, a name, then `.key`s. Whether the name is a root is the
 * orchestrator's question, so a misspelt one is still an address — one that resolves to nothing and says so — never text.
 */
const WHOLE_ADDRESS = /^\$([A-Za-z]+)((?:\.[A-Za-z0-9_]+)*)$/;

/** The same shape written into a text, delimited by `$$` on both sides. */
const EMBEDDED_ADDRESS = /\$\$([A-Za-z]+)((?:\.[A-Za-z0-9_]+)*)\$\$/g;

/** A string that is one whole address, or `null` for anything else. */
export function parseSchemaAddress(value: string): SchemaAddress | null {
  const match = WHOLE_ADDRESS.exec(value);

  return match ? { text: value, root: match[1], keys: keysOf(match[2]) } : null;
}

/** Every `$$…$$` in a text, in order. */
export function findSchemaPlaceholders(value: string): SchemaAddress[] {
  return [...value.matchAll(EMBEDDED_ADDRESS)].map(([text, root, keys]) => ({ text, root, keys: keysOf(keys) }));
}

/** The text with every `$$…$$` replaced by what `replace` answers for it — through a function, so a `$$` it answers stays literal. */
export function replaceSchemaPlaceholders(value: string, replace: (placeholder: SchemaAddress) => string): string {
  return value.replace(EMBEDDED_ADDRESS, (text: string, root: string, keys: string) => replace({ text, root, keys: keysOf(keys) }));
}

/**
 * The dot walk underneath every address, and what `dataSource.key` slices an inherited state with.
 *
 * A missing key, or a primitive on the way to one, resolves to `undefined` rather than throwing: an optional field absent
 * from the data is ordinary, and a resolver that reported it would report on every render of every schema that has one.
 */
export function readValueAtPath(source: unknown, path: string): unknown {
  return readValueAtKeys(source, path.split('.'));
}

/** The same walk over keys already split. No keys is the source itself. */
export function readValueAtKeys(source: unknown, keys: readonly string[]): unknown {
  let current = source;

  for (const key of keys) {
    if (current === null || typeof current !== 'object') {
      return undefined;
    }

    current = (current as Record<string, unknown>)[key];
  }

  return current;
}

function keysOf(suffix: string): string[] {
  return suffix ? suffix.slice(1).split('.') : [];
}
